import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Link } from "react-router-dom";
import { 
  Compass, 
  Wallet, 
  QrCode, 
  User, 
  ChevronDown, 
  Check, 
  ChevronRight, 
  Menu, 
  X, 
  ArrowRight, 
  Award, 
  MapPin, 
  Lock, 
  Mail, 
  Eye, 
  Smartphone,
  Star,
  Sparkles
} from "lucide-react";
import "@/App.css";

import PodminkyPouzivani from "./pages/PodminkyPouzivani";
import OchranaOsobnichUdaju from "./pages/OchranaOsobnichUdaju";
import PodminkyProPartnery from "./pages/PodminkyProPartnery";
import Podpora from "./pages/Podpora";

// Source of truth app images from assets & design guidelines
const IMAGES = {
  splash: "https://customer-assets-7cd3h4nn.emergentagent.net/job_610d15ac-2f48-4317-87e2-b74509e7864c/artifacts/1svylbmq_image.webp",
  discover: "https://customer-assets-7cd3h4nn.emergentagent.net/job_610d15ac-2f48-4317-87e2-b74509e7864c/artifacts/kmdy9qw6_image.webp",
  cards: "https://customer-assets-7cd3h4nn.emergentagent.net/job_610d15ac-2f48-4317-87e2-b74509e7864c/artifacts/aw2riwv8_image.webp",
  detail: "https://customer-assets-7cd3h4nn.emergentagent.net/job_610d15ac-2f48-4317-87e2-b74509e7864c/artifacts/7xbb0wqd_image.webp",
  business: "https://customer-assets-7cd3h4nn.emergentagent.net/job_610d15ac-2f48-4317-87e2-b74509e7864c/artifacts/ug5bdztf_image.png",
  brnoCity: "https://images.unsplash.com/photo-1684477167048-09ffb15f5f44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxicm5vJTIwY3plY2glMjByZXB1YmxpYyUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODY4OTkyODB8MA&ixlib=rb-4.1.0&q=85"
};

// Navigation Hook for Cross-Page scrolling
export function useAppNavigation() {
  const navigate = useNavigate();
  const location = useLocation();

  const handleScrollTo = (id) => {
    if (location.pathname === "/") {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate("/#" + id);
    }
  };

  return { handleScrollTo, pathname: location.pathname };
}

// 2. STICKY HEADER COMPONENT
export function Header({ handleScrollTo, pathname }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (pathname === "/") {
      handleScrollTo("vernost");
    } else {
      navigate("/");
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-white border-b border-[#EEEEEE]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        {/* Logo element */}
        <a 
          href="/" 
          onClick={handleLogoClick}
          className="flex items-center group transition-transform duration-200 hover:scale-[1.02]"
          data-testid="header-logo-link"
        >
          <img 
            src="/logo_light.png" 
            alt="Věrnost" 
            className="h-8 w-auto object-contain" 
            data-testid="header-logo-text"
          />
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-8">
          <a 
            href="#jak-to-funguje" 
            onClick={(e) => { e.preventDefault(); handleScrollTo("jak-to-funguje"); }}
            className="text-sm font-semibold text-neutral-600 hover:text-[#FF5A00] transition-colors duration-200"
            data-testid="nav-link-jak-to-funguje"
          >
            Jak to funguje
          </a>
          <a 
            href="#premium" 
            onClick={(e) => { e.preventDefault(); handleScrollTo("premium"); }}
            className="text-sm font-semibold text-neutral-600 hover:text-[#FF5A00] transition-colors duration-200"
            data-testid="nav-link-premium"
          >
            Premium
          </a>
          <a 
            href="#pro-podniky" 
            onClick={(e) => { e.preventDefault(); handleScrollTo("pro-podniky"); }}
            className="text-sm font-semibold text-neutral-600 hover:text-[#FF5A00] transition-colors duration-200"
            data-testid="nav-link-pro-podniky"
          >
            Pro podniky
          </a>
          <a 
            href="#faq" 
            onClick={(e) => { e.preventDefault(); handleScrollTo("faq"); }}
            className="text-sm font-semibold text-neutral-600 hover:text-[#FF5A00] transition-colors duration-200"
            data-testid="nav-link-faq"
          >
            Otázky
          </a>
        </nav>

        {/* Header CTA */}
        <div className="hidden md:block">
          <a 
            href="https://app.vernostapp.cz"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block text-center px-6 py-2.5 rounded-full bg-[#FF5A00] hover:bg-[#E04F00] text-white text-sm font-bold shadow-sm transition-all duration-200 hover:scale-[1.03] active:scale-[0.97]"
            data-testid="header-cta-button"
          >
            Otevřít aplikaci
          </a>
        </div>

        {/* Mobile hamburger toggle */}
        <button 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden p-2 rounded-full hover:bg-neutral-100 transition-colors"
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Navigation Drawer */}
      <div 
        className={`md:hidden absolute top-20 left-0 w-full bg-white border-b border-neutral-150 shadow-lg overflow-hidden transition-all duration-300 ease-in-out ${
          mobileMenuOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0 pointer-events-none"
        }`}
        data-testid="mobile-menu-container"
      >
        <div className="px-4 pt-4 pb-8 space-y-4 flex flex-col text-left">
          <a 
            href="#jak-to-funguje" 
            onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); handleScrollTo("jak-to-funguje"); }}
            className="text-lg font-bold text-neutral-800 py-2 border-b border-neutral-100"
            data-testid="mobile-link-jak-to-funguje"
          >
            Jak to funguje
          </a>
          <a 
            href="#premium" 
            onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); handleScrollTo("premium"); }}
            className="text-lg font-bold text-neutral-800 py-2 border-b border-neutral-100"
            data-testid="mobile-link-premium"
          >
            Premium
          </a>
          <a 
            href="#pro-podniky" 
            onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); handleScrollTo("pro-podniky"); }}
            className="text-lg font-bold text-neutral-800 py-2 border-b border-neutral-100"
            data-testid="mobile-link-pro-podniky"
          >
            Pro podniky
          </a>
          <a 
            href="#faq" 
            onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); handleScrollTo("faq"); }}
            className="text-lg font-bold text-neutral-800 py-2 border-b border-neutral-100"
            data-testid="mobile-link-faq"
          >
            Otázky
          </a>
          <a 
            href="https://app.vernostapp.cz"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMobileMenuOpen(false)}
            className="inline-block text-center w-full mt-4 py-3 bg-[#FF5A00] hover:bg-[#E04F00] text-white rounded-full font-bold shadow-md transition-all active:scale-[0.95]"
            data-testid="mobile-cta-button"
          >
            Otevřít aplikaci
          </a>
        </div>
      </div>
    </header>
  );
}

// 12. FOOTER COMPONENT
export function Footer({ handleScrollTo, pathname }) {
  const navigate = useNavigate();

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (pathname === "/") {
      handleScrollTo("vernost");
    } else {
      navigate("/");
    }
  };

  return (
    <footer className="bg-neutral-950 text-neutral-400 border-t border-neutral-900 pt-10 md:pt-12 pb-5 md:pb-6 relative z-10 text-left">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-1">
          
          {/* Logo and short description - 4 cols */}
          <div className="md:col-span-4 flex flex-col space-y-4">
            <a 
              href="/" 
              onClick={handleLogoClick}
              className="flex items-center self-start group transition-transform duration-200 hover:scale-[1.02]"
              data-testid="footer-logo-link"
            >
              <img 
                src="/logo_dark.png" 
                alt="Věrnost" 
                className="h-7 w-auto object-contain" 
              />
            </a>
            <p className="text-xs text-neutral-500 font-bold max-w-xs">
              Moderní česká mobilní aplikace pro digitalizaci věrnostních programů lokálních podniků.
            </p>
          </div>

          {/* Links Columns - 8 cols */}
          <div className="md:col-span-8 grid grid-cols-2 sm:grid-cols-3 gap-8">
            
            {/* Product Links */}
            <div className="flex flex-col space-y-3">
              <span className="text-xs font-black tracking-widest text-white uppercase mb-2">Aplikace</span>
              <a href="#vernost" onClick={(e) => { e.preventDefault(); handleScrollTo("vernost"); }} className="text-xs font-bold text-neutral-400 hover:text-[#FF5A00] transition-colors" data-testid="footer-link-vernost">Věrnost</a>
              <a href="#jak-to-funguje" onClick={(e) => { e.preventDefault(); handleScrollTo("jak-to-funguje"); }} className="text-xs font-bold text-neutral-400 hover:text-[#FF5A00] transition-colors" data-testid="footer-link-jak-to-funguje">Jak to funguje</a>
              <a href="#premium" onClick={(e) => { e.preventDefault(); handleScrollTo("premium"); }} className="text-xs font-bold text-neutral-400 hover:text-[#FF5A00] transition-colors" data-testid="footer-link-premium">Premium</a>
              <a href="#pro-podniky" onClick={(e) => { e.preventDefault(); handleScrollTo("pro-podniky"); }} className="text-xs font-bold text-neutral-400 hover:text-[#FF5A00] transition-colors" data-testid="footer-link-pro-podniky">Pro podniky</a>
              <a href="#faq" onClick={(e) => { e.preventDefault(); handleScrollTo("faq"); }} className="text-xs font-bold text-neutral-400 hover:text-[#FF5A00] transition-colors" data-testid="footer-link-faq">Časté otázky</a>
            </div>

            {/* Legal Links */}
            <div className="flex flex-col space-y-3">
              <span className="text-xs font-black tracking-widest text-white uppercase mb-2">Právní</span>
              <Link to="/podminky-pouzivani" className="text-xs font-bold text-neutral-400 hover:text-[#FF5A00] transition-colors text-left" data-testid="footer-link-podminky">Podmínky používání</Link>
              <Link to="/ochrana-osobnich-udaju" className="text-xs font-bold text-neutral-400 hover:text-[#FF5A00] transition-colors text-left" data-testid="footer-link-ochrana">Ochrana osobních údajů</Link>
              <Link to="/podminky-pro-partnery" className="text-xs font-bold text-neutral-400 hover:text-[#FF5A00] transition-colors text-left" data-testid="footer-link-partneri">Podmínky pro partnery</Link>
            </div>

            {/* Contact Link */}
            <div className="flex flex-col space-y-3">
              <span className="text-xs font-black tracking-widest text-white uppercase mb-2">Kontakt</span>
              <Link to="/podpora" className="text-xs font-bold text-neutral-400 hover:text-[#FF5A00] transition-colors text-left" data-testid="footer-link-podpora">Podpora</Link>
              <a href="mailto:podpora@vernostapp.cz" className="text-xs font-bold text-neutral-400 hover:text-[#FF5A00] transition-colors text-left" data-testid="footer-link-email">E-mail: podpora@vernostapp.cz</a>
              <span className="text-xs font-bold text-neutral-500 mt-2">Brno, Česká republika</span>
            </div>

          </div>

        </div>

        {/* Copyright Divider */}
        <div className="pt-3 border-t border-neutral-900 flex flex-col space-y-1 text-xs text-neutral-600 font-bold">
          <div>© 2026 Věrnost. Všechna práva vyhrazena.</div>
          <div>
            Design, vývoj & marketing —{" "}
            <a 
              href="https://www.instagram.com/ivancizinec/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-neutral-500 hover:text-[#FF5A00] transition-colors duration-200"
            >
              Ivan Kuznetsov
            </a>
          </div>
        </div>

      </div>
    </footer>
  );
}

// LANDING PAGE COMPONENT
function LandingPage() {
  const { handleScrollTo, pathname } = useAppNavigation();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaderVisible, setIsLoaderVisible] = useState(false);
  const [faqOpen, setFaqOpen] = useState({});
  const [activeStep, setActiveStep] = useState(1);
  const [isPremiumModalOpen, setIsPremiumModalOpen] = useState(false);
  const [activeScreenshot, setActiveScreenshot] = useState(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setActiveScreenshot(null);
      }
    };
    if (activeScreenshot) {
      window.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [activeScreenshot]);

  useEffect(() => {
    document.title = "Věrnost";
  }, []);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        const timer = setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 150);
        return () => clearTimeout(timer);
      }
    }
  }, [location.hash]);

  const toggleFaq = (index) => {
    setFaqOpen(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] text-[#111111] font-sans antialiased selection:bg-[#FF5A00]/10 selection:text-[#FF5A00]">
      {/* 1. INITIAL SPLASH LOADER (Disabled by default) */}
      {isLoaderVisible && (
        <div 
          className={`fixed inset-0 z-50 flex flex-col items-center justify-center bg-white transition-all duration-700 ease-in-out ${
            isLoading ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"
          }`}
          data-testid="splash-loader"
        >
          <div className="flex flex-col items-center justify-center space-y-6">
            <div className="flex items-center">
              <img 
                src="/logo_light.png" 
                alt="Věrnost" 
                className="h-10 w-auto object-contain" 
              />
            </div>
            <div className="relative w-10 h-10">
              <div className="absolute inset-0 rounded-full border-4 border-neutral-100" />
              <div className="absolute inset-0 rounded-full border-4 border-t-[#FF5A00] animate-spin" />
            </div>
          </div>
        </div>
      )}

      {/* sticky header wrapper */}
      <Header handleScrollTo={handleScrollTo} pathname={pathname} />

      {/* 3. HERO SECTION */}
      <section id="vernost" className="relative py-16 md:py-28 overflow-hidden bg-gradient-to-b from-white to-neutral-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Col - Copy */}
            <div className="lg:col-span-7 flex flex-col space-y-8 lg:pr-6 text-left">
              {/* Headline */}
              <div>
                <h1 
                  className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-normal text-neutral-900 leading-[1.05]"
                  data-testid="hero-heading"
                >
                  Všechny vaše věrnostní kartičky<br />{" "}
                  <span className="text-[#FF5A00]">jednoduše na jednom místě.</span>
                </h1>
              </div>

              {/* Subtitle */}
              <p 
                className="text-lg md:text-xl font-medium text-neutral-600 leading-relaxed max-w-2xl"
                data-testid="hero-subtitle"
              >
                Objevujte podniky ve svém okolí, sbírejte digitální razítka a užívejte si odměny za svou věrnost.
              </p>

              {/* CTAs */}
              <div className="flex flex-wrap gap-4 items-center">
                {/* Mobile App Store */}
                <a 
                  href="https://apps.apple.com/cz/app/v%C4%9Brnost/id6807436725?l=cs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-12 px-5 rounded-full bg-neutral-900 hover:bg-neutral-800 text-white text-sm font-bold flex items-center space-x-2.5 shadow-md border border-neutral-800 transition-all duration-200 hover:scale-[1.03] active:scale-[0.97]"
                  data-testid="download-app-store"
                >
                  <svg className="w-4 h-4 fill-current shrink-0 text-white" viewBox="0 0 24 24">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.07 2.47.3 3.64 2.1-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42.14-1.38 1.1-1 .99-2 .98-2 .98-1.5 0-1.7-.79-3.29-.79-1.59 0-1.8.79-3.29.79-1.49 0-2.32-1.31-3.29-2.53M15.97 4.17c.66-.81 1.11-1.93.99-3.06-1 .04-2.11.67-2.83 1.49-.62.71-1.16 1.85-1.02 2.96 1.1.09 2.2-.58 2.86-1.39"/>
                  </svg>
                  <span className="text-white">App Store</span>
                </a>

                {/* Web App CTA */}
                <a 
                  href="https://app.vernostapp.cz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-12 px-6 rounded-full bg-[#FF5A00] hover:bg-[#E04F00] text-white text-sm font-black transition-all duration-200 hover:scale-[1.03] active:scale-[0.97] shadow-md flex items-center space-x-2 border border-[#FF5A00] hover:border-[#E04F00]"
                  data-testid="open-web-app"
                >
                  <span>Otevřít webovou aplikaci</span>
                  <svg className="w-4 h-4 shrink-0 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Right Col - High-Fidelity Modern App Mockup */}
            <div className="lg:col-span-5 flex justify-center mt-12 lg:mt-0 relative z-10">
              {/* Subtle back-glow in brand orange */}
              <div className="absolute -inset-10 bg-gradient-to-tr from-[#FF5A00]/15 to-orange-400/5 rounded-[40px] blur-3xl opacity-50 pointer-events-none" />

              {/* Ultra-polished modern smartphone frame */}
              <div 
                className="relative w-[310px] sm:w-[350px] h-[630px] sm:h-[710px] rounded-[50px] bg-neutral-900 p-3 shadow-2xl border-[6px] border-neutral-800 flex flex-col overflow-hidden transition-transform duration-300 hover:scale-[1.01]"
                data-testid="hero-smartphone-mockup"
              >
                {/* Dynamic Island / Speaker cutout */}
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 w-28 h-4 bg-neutral-900 rounded-full z-30 flex items-center justify-center border border-neutral-800/40">
                  <div className="w-1.5 h-1.5 bg-neutral-800 rounded-full mr-2" />
                  <div className="w-12 h-1 bg-neutral-800 rounded-full" />
                </div>

                {/* Inner screen - pure app screenshot with no HTML overlays */}
                <div className="relative w-full h-full rounded-[38px] overflow-hidden bg-white shadow-inner">
                  <img 
                    src={IMAGES.discover} 
                    alt="Aplikace Věrnost - Objevovat" 
                    className="w-full h-full object-cover select-none pointer-events-none"
                    data-testid="hero-app-screenshot"
                  />
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 5. JAK TO FUNGUJE SECTION */}
      <section id="jak-to-funguje" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            
            {/* Left Column (Heading + Steps) - 7 cols on large */}
            <div className="lg:col-span-7 flex flex-col text-left">
              
              {/* Heading */}
              <div className="mb-12">
                <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-normal text-neutral-900" data-testid="jak-to-funguje-heading">
                  Jak Věrnost funguje?
                </h2>
              </div>

              {/* Steps Column */}
              <div className="flex flex-col space-y-6">
                
                {/* Step 1 */}
                <div 
                  onMouseEnter={() => setActiveStep(1)}
                  onClick={() => setActiveStep(1)}
                  className="cursor-pointer p-8 rounded-3xl border border-neutral-200 bg-[#FDFDFD] transition-all duration-300 flex flex-col sm:flex-row items-start sm:space-x-6 text-left hover:bg-orange-50/10 hover:border-[#FF5A00]/30 hover:shadow-md hover:scale-[1.01] group"
                  data-testid="step-card-1"
                >
                  <span className="text-5xl sm:text-6xl font-heading font-black text-neutral-200 group-hover:text-[#FF5A00] transition-colors mb-4 sm:mb-0">01</span>
                  <div>
                    <h3 className="text-xl font-extrabold text-neutral-900 mb-2">Vyberte si podnik</h3>
                    <p className="text-neutral-500 font-medium">
                      Najděte podnik ve svém okolí a otevřete jeho věrnostní program.
                    </p>
                  </div>
                </div>

                {/* Step 2 */}
                <div 
                  onMouseEnter={() => setActiveStep(2)}
                  onClick={() => setActiveStep(2)}
                  className="cursor-pointer p-8 rounded-3xl border border-neutral-200 bg-[#FDFDFD] transition-all duration-300 flex flex-col sm:flex-row items-start sm:space-x-6 text-left hover:bg-orange-50/10 hover:border-[#FF5A00]/30 hover:shadow-md hover:scale-[1.01] group"
                  data-testid="step-card-2"
                >
                  <span className="text-5xl sm:text-6xl font-heading font-black text-neutral-200 group-hover:text-[#FF5A00] transition-colors mb-4 sm:mb-0">02</span>
                  <div>
                    <h3 className="text-xl font-extrabold text-neutral-900 mb-2">Sbírejte razítka</h3>
                    <p className="text-neutral-500 font-medium">
                      Při návštěvě stačí ukázat svůj osobní QR kód. Obsluha jej naskenuje a razítko se vám automaticky přidá na věrnostní kartu.
                    </p>
                  </div>
                </div>

                {/* Step 3 */}
                <div 
                  onMouseEnter={() => setActiveStep(3)}
                  onClick={() => setActiveStep(3)}
                  className="cursor-pointer p-8 rounded-3xl border border-neutral-200 bg-[#FDFDFD] transition-all duration-300 flex flex-col sm:flex-row items-start sm:space-x-6 text-left hover:bg-orange-50/10 hover:border-[#FF5A00]/30 hover:shadow-md hover:scale-[1.01] group"
                  data-testid="step-card-3"
                >
                  <span className="text-5xl sm:text-6xl font-heading font-black text-neutral-200 group-hover:text-[#FF5A00] transition-colors mb-4 sm:mb-0">03</span>
                  <div>
                    <h3 className="text-xl font-extrabold text-neutral-900 mb-2">Získejte odměnu</h3>
                    <p className="text-neutral-500 font-medium">
                      Sbírejte razítka a po dosažení stanoveného počtu se vám odměna automaticky odemkne. Pak už ji stačí u podniku využít.
                    </p>
                  </div>
                </div>

              </div>
            </div>

            {/* Right Col - High-Fidelity App Screen Mockup inside Phone Frame */}
            <div className="lg:col-span-5 flex justify-center items-start lg:pt-2 relative z-10 mt-12 lg:mt-0">
              {/* Subtle back-glow in brand orange */}
              <div className="absolute -inset-8 bg-gradient-to-tr from-[#FF5A00]/10 to-orange-400/5 rounded-[40px] blur-2xl opacity-40 pointer-events-none" />

              {/* Ultra-polished modern smartphone frame (scaled up by ~15-20%) */}
              <div 
                className="relative w-[285px] sm:w-[330px] h-[580px] sm:h-[670px] rounded-[48px] bg-neutral-900 p-2.5 shadow-2xl border-[5px] border-neutral-800 flex flex-col overflow-hidden transition-transform duration-300 hover:scale-[1.01]"
                data-testid="jak-to-funguje-mockup"
              >
                {/* Dynamic Island / Speaker cutout */}
                <div className="absolute top-3 left-1/2 transform -translate-x-1/2 w-24 h-3.5 bg-neutral-900 rounded-full z-30 flex items-center justify-center border border-neutral-800/40">
                  <div className="w-1.5 h-1.5 bg-neutral-800 rounded-full mr-2" />
                  <div className="w-10 h-1 bg-neutral-800 rounded-full" />
                </div>

                {/* Inner screen - 3 layered images for transition */}
                <div className="relative w-full h-full rounded-[36px] overflow-hidden bg-white shadow-inner">
                  {/* Screenshot 01 */}
                  <img 
                    src="/ss_don_pacinko_detail.webp" 
                    alt="01 — Vyberte si podnik" 
                    className={`absolute inset-0 w-full h-full object-cover select-none pointer-events-none transition-opacity duration-500 ease-in-out ${
                      activeStep === 1 ? "opacity-100 z-10" : "opacity-0 z-0"
                    }`}
                  />
                  {/* Screenshot 02 */}
                  <img 
                    src="/ss_qr_demo.webp" 
                    alt="02 — Sbírejte razítka" 
                    className={`absolute inset-0 w-full h-full object-cover select-none pointer-events-none transition-opacity duration-500 ease-in-out ${
                      activeStep === 2 ? "opacity-100 z-10" : "opacity-0 z-0"
                    }`}
                  />
                  {/* Screenshot 03 */}
                  <img 
                    src="/ss_moje_karty.webp" 
                    alt="03 — Získejte odměnu" 
                    className={`absolute inset-0 w-full h-full object-cover select-none pointer-events-none transition-opacity duration-500 ease-in-out ${
                      activeStep === 3 ? "opacity-100 z-10" : "opacity-0 z-0"
                    }`}
                  />
                </div>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* 8. PREMIUM PRICING SECTION */}
      <section id="premium" className="py-20 md:py-32 bg-[#F9F9F9] border-t border-neutral-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="max-w-3xl mx-auto text-center mb-16 space-y-4">
            <span className="text-[#FF5A00] text-sm font-bold tracking-widest uppercase">Vyberte si svůj tarif</span>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-normal text-neutral-900" data-testid="premium-heading">
              Více podniků. Více odměn.
            </h2>
            <p className="text-neutral-500 font-medium max-w-xl mx-auto">
              Začněte zdarma, nebo si odemkněte všechny možnosti Věrnost s Premium.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            
            {/* Free Plan */}
            <div 
              className="bg-white rounded-3xl p-8 border border-neutral-200 text-left flex flex-col justify-between"
              data-testid="pricing-plan-zdarma"
            >
              <div>
                <span className="px-3 py-1 bg-neutral-100 rounded-full text-[10px] font-bold uppercase tracking-wider text-neutral-500 inline-block mb-4">Základní tarif</span>
                <h3 className="text-2xl font-black text-neutral-900 mb-2">Free</h3>
                <p className="text-xs text-neutral-400 font-bold mb-6">Pro jednoduché používání Věrnost</p>
                
                <div className="flex items-baseline mb-8">
                  <span className="text-4xl font-heading font-black text-neutral-900">0 Kč</span>
                  <span className="text-neutral-400 font-bold text-xs ml-2">navždy</span>
                </div>

                <div className="space-y-4 mb-8">
                  <div className="flex items-center space-x-3 text-neutral-700 text-sm">
                    <Check className="w-5 h-5 text-neutral-400 shrink-0" />
                    <span><strong>neomezený počet</strong> věrnostních programů</span>
                  </div>
                  <div className="flex items-center space-x-3 text-neutral-700 text-sm">
                    <Check className="w-5 h-5 text-neutral-400 shrink-0" />
                    <span>digitální věrnostní kartičky</span>
                  </div>
                  <div className="flex items-center space-x-3 text-neutral-700 text-sm">
                    <Check className="w-5 h-5 text-neutral-400 shrink-0" />
                    <span>objevování podniků</span>
                  </div>
                  <div className="flex items-center space-x-3 text-neutral-700 text-sm">
                    <Check className="w-5 h-5 text-neutral-400 shrink-0" />
                    <span>osobní QR kód</span>
                  </div>
                </div>
              </div>

              <button 
                onClick={() => handleScrollTo("vernost")}
                className="w-full py-4 rounded-full bg-neutral-100 hover:bg-neutral-200 text-neutral-800 text-sm font-bold tracking-wide transition-all"
                data-testid="plan-button-zdarma"
              >
                Používat zdarma
              </button>
            </div>

            {/* Premium Plan (Visually highlighted) */}
            <div 
              className="relative bg-neutral-900 text-white rounded-3xl p-8 border border-[#FF5A00]/40 shadow-xl text-left flex flex-col justify-between overflow-hidden"
              data-testid="pricing-plan-premium"
            >
              {/* Premium Glow effect */}
              <div className="absolute top-[-100px] right-[-100px] w-64 h-64 bg-[#FF5A00]/10 rounded-full blur-3xl" />
              
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <span className="px-3 py-1 bg-[#FF5A00] text-white rounded-full text-[10px] font-black uppercase tracking-wider inline-block">Doporučeno</span>
                </div>

                <h3 className="text-2xl font-black mb-2">Premium</h3>
                <p className="text-xs text-neutral-400 font-bold mb-6">Věrnost bez omezení</p>
                
                <div className="flex items-baseline mb-8">
                  <span className="text-4xl font-heading font-black text-[#FF5A00]" data-testid="premium-price-text">99 Kč</span>
                  <span className="text-neutral-400 font-bold text-xs ml-2">/ měsíc</span>
                </div>

                <div className="space-y-4 mb-8">
                  <div className="flex items-center space-x-3 text-neutral-200 text-sm">
                    <Check className="w-5 h-5 text-[#FF5A00] shrink-0" />
                    <span>všechny funkce Věrnost Free</span>
                  </div>
                  <div className="flex items-center space-x-3 text-neutral-200 text-sm">
                    <Check className="w-5 h-5 text-[#FF5A00] shrink-0" />
                    <span>Premium návštěvy</span>
                  </div>
                  <div className="flex items-center space-x-3 text-neutral-200 text-sm">
                    <Check className="w-5 h-5 text-[#FF5A00] shrink-0" />
                    <span>prémiové kupóny a nabídky</span>
                  </div>
                </div>
              </div>

              <button 
                onClick={() => setIsPremiumModalOpen(true)}
                className="relative z-10 w-full py-4 rounded-full bg-[#FF5A00] hover:bg-[#E04F00] text-white text-sm font-bold tracking-wide transition-all shadow-lg shadow-orange-500/20 active:scale-[0.98]"
                data-testid="plan-button-premium"
              >
                Získat Premium
              </button>
            </div>

          </div>

        </div>
      </section>

      {/* 9. PRO PODNIKY SECTION (DARK THEME FOR CONTRAST) */}
      <section id="pro-podniky" className="py-20 md:py-32 bg-[#111111] text-white relative overflow-hidden">
        
        {/* Subtle grid elements in background */}
        <div className="absolute inset-0 opacity-[0.02] pointer-events-none bg-[linear-gradient(to_right,#808080_1px,transparent_1px),linear-gradient(to_bottom,#808080_1px,transparent_1px)] bg-[size:24px_24px]" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            
            {/* Left Col - Business Pitch */}
            <div className="lg:col-span-6 flex flex-col space-y-6 text-left relative z-10">
              <span className="text-[#FF5A00] text-sm font-bold tracking-widest uppercase">Pro majitele podniků</span>
              <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-normal text-white leading-tight" data-testid="pro-podniky-heading">
                Máte vlastní podnik?
              </h2>
              <p className="text-lg text-neutral-400 font-medium leading-relaxed">
                Vytvořte si vlastní digitální věrnostní program a odměňujte zákazníky za jejich opakované návštěvy. Vše jednoduše na jednom místě.
              </p>

              {/* Business features */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4 text-left">
                <div className="p-4 bg-neutral-900 rounded-2xl border border-neutral-800">
                  <h4 className="text-sm font-extrabold text-white mb-1">Věrnostní program</h4>
                  <p className="text-xs text-neutral-500">Nastavte si vlastní odměny, vzhled kartičky and vlastní razítka.</p>
                </div>
                <div className="p-4 bg-neutral-900 rounded-2xl border border-neutral-800">
                  <h4 className="text-sm font-extrabold text-white mb-1">QR a zaměstnanci</h4>
                  <p className="text-xs text-neutral-500">Načítejte zákazníky přes QR a spravujte přístupy svých zaměstnanců.</p>
                </div>
                <div className="p-4 bg-neutral-900 rounded-2xl border border-neutral-800">
                  <h4 className="text-sm font-extrabold text-white mb-1">Kupóny</h4>
                  <p className="text-xs text-neutral-500">Vytvářejte a spravujte kupóny pro své zákazníky.</p>
                </div>
                <div className="p-4 bg-neutral-900 rounded-2xl border border-neutral-800">
                  <h4 className="text-sm font-extrabold text-white mb-1">Historie aktivit</h4>
                  <p className="text-xs text-neutral-500">Mějte přehled o razítkách, návštěvách, kupónech a aktivitách zaměstnanců.</p>
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-wrap gap-4 pt-4">
                <a 
                  href="https://business.vernostapp.cz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-[#FF5A00] hover:bg-[#E04F00] text-white text-sm font-bold rounded-full transition-all inline-block hover:scale-[1.02] active:scale-[0.98]"
                  data-testid="business-primary-cta"
                >
                  Věrnost pro podniky
                </a>
              </div>
            </div>

            {/* Right Col - High Fidelity Desktop Web Mockups */}
            <div className="lg:col-span-6 relative z-10 flex flex-col items-center justify-center space-y-5 py-6">
              
              {/* Top Screenshot: Login Screen */}
              <div 
                onClick={() => setActiveScreenshot({ src: "/business_screenshot_full.png", alt: "Věrnost Business – Přihlášení" })}
                className="w-full max-w-[500px] rounded-xl overflow-hidden shadow-md border border-neutral-800/10 bg-white p-1 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl cursor-pointer"
                data-testid="business-login-screenshot-container"
              >
                <img 
                  src="/business_screenshot_full.png" 
                  alt="Věrnost Business – Přihlášení" 
                  className="w-full h-auto object-contain select-none pointer-events-none rounded-lg"
                  data-testid="business-login-screenshot"
                />
              </div>

              {/* Bottom Screenshot: Overview Dashboard Screen */}
              <div 
                onClick={() => setActiveScreenshot({ src: "/business_overview.png", alt: "Věrnost Business – Přehled" })}
                className="w-full max-w-[500px] rounded-xl overflow-hidden shadow-md border border-neutral-800/10 bg-white p-1 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl cursor-pointer"
                data-testid="business-overview-screenshot-container"
              >
                <img 
                  src="/business_overview.png" 
                  alt="Věrnost Business – Přehled" 
                  className="w-full h-auto object-contain select-none pointer-events-none rounded-lg"
                  data-testid="business-overview-screenshot"
                />
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* 10. FAQ SECTION */}
      <section id="faq" className="py-20 md:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center mb-10 space-y-4">
            <span className="text-[#FF5A00] text-sm font-bold tracking-widest uppercase">Zajímá vás více?</span>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-normal text-neutral-900" data-testid="faq-heading">
              Časté otázky
            </h2>
          </div>

          {/* Accordion Questions */}
          <div className="space-y-2.5 text-left" data-testid="faq-accordion">
            {[
              {
                q: "Je Věrnost zdarma?",
                a: "Ano. Stažení aplikace a používání základních funkcí Věrnost je zdarma. S tarifem „Free“ můžete současně využívat až 2 věrnostní programy. Premium za 99 Kč měsíčně nabízí neomezený počet věrnostních programů, Premium návštěvy a prémiové kupóny."
              },
              {
                q: "Jak získám razítko?",
                a: "Při návštěvě zapojeného podniku ukážete obsluze svůj osobní QR kód v aplikaci. Obsluha jej naskenuje a razítko se vám automaticky připíše na příslušnou věrnostní kartu."
              },
              {
                q: "Co se stane, když opustím věrnostní program podniku?",
                a: "Pokud věrnostní program opustíte, všechna nasbíraná razítka v tomto programu se vynulují. Pokud se k programu později znovu připojíte, začínáte sbírat razítka od začátku."
              },
              {
                q: "Co se stane, když přejdu z „Premium“ na tarif „Free“?",
                a: "Po přechodu na tarif „Free“ si vyberete 2 věrnostní programy, které chcete nadále používat. U ostatních programů se nasbíraná razítka vynulují. Nasbírané Premium návštěvy vám zůstanou, ale bez aktivního Premium je nelze využít k získání prémiových kupónů."
              },
              {
                q: "Jak se může můj podnik zapojit do Věrnost?",
                a: "Registrace podniku je jednoduchá. Přejděte do sekce „Pro podniky“ a klikněte na tlačítko „Věrnost pro podniky“. Následně si můžete vytvořit účet a po ověření administrátorem nastavit vlastní věrnostní program."
              }
            ].map((item, idx) => (
              <div 
                key={idx} 
                className="border-b border-neutral-100 pb-2.5 transition-colors"
                data-testid={`faq-item-${idx}`}
              >
                <button 
                  onClick={() => toggleFaq(idx)}
                  className="w-full py-2.5 flex items-center justify-between font-extrabold text-neutral-800 text-left hover:text-[#FF5A00] transition-colors"
                  data-testid={`faq-trigger-${idx}`}
                >
                  <span className="text-base sm:text-lg">{item.q}</span>
                  <ChevronDown className={`w-5 h-5 text-neutral-400 shrink-0 transition-transform duration-300 ${faqOpen[idx] ? "rotate-180 text-[#FF5A00]" : ""}`} />
                </button>
                
                <div 
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    faqOpen[idx] ? "max-h-[300px] opacity-100 mt-1 pb-3" : "max-h-0 opacity-0 pointer-events-none"
                  }`}
                  data-testid={`faq-content-${idx}`}
                >
                  <p className="text-sm sm:text-base text-neutral-500 font-medium leading-relaxed">
                    {item.a}
                  </p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>



      {/* 12. JAK VZNIKLA VĚRNOST SECTION */}
      <section id="jak-vznikla-vernost" className="py-20 md:py-32 bg-white border-t border-neutral-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Column - Copy */}
            <div className="lg:col-span-7 flex flex-col space-y-6 text-left">
              <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-normal text-neutral-900" data-testid="about-heading">
                Jak vznikla Věrnost
              </h2>
              
              <div className="space-y-4 text-base sm:text-lg text-neutral-600 leading-relaxed font-medium">
                <p>
                  Jmenuji se Ivan a v gastronomii podnikám už skoro tři roky. Moje cesta začala malou palačinkárnou, kde jsem hodně pracoval a snažil se ji postupně rozvíjet. Právě tam jsem pochopil, jak důležité je budovat vztah se zákazníky a dát jim důvod se vracet.
                </p>
                <p>
                  Za tu dobu jsem nechal vytisknout tisíce věrnostních kartiček a spoustu papírových kuponů. Občas se stávalo, že stovky kartiček došly už během několika dní. Po otevření druhé pobočky to ale začalo být ještě komplikovanější.
                </p>
                <p>
                  Zároveň jsem si při rozhovorech se zákazníky často všímal, kolik různých věrnostních kartiček u sebe mají. Najít mezi nimi zrovna tu správnou není vždy pohodlné a někdy ji člověk nemá u sebe vůbec.
                </p>
                <p>
                  A tak jsem postupně přišel s tímto nápadem. Chtěl jsem vytvořit co nejjednodušší aplikaci, která spojuje věrnostní programy na jednom místě a zároveň propojuje lokální podniky. Líbí se mi myšlenka, že vedle konkurence mezi podniky může existovat i vzájemná podpora.
                </p>
                <p>
                  Věrnost vzniká bez externích investorů. Aplikaci postupně financuji z vlastních prostředků a dávám do ní veškerou svou energii, nadšení a čas.
                </p>
                <p className="text-lg sm:text-xl font-bold text-neutral-900 border-l-4 border-[#FF5A00] pl-4 py-1.5 mt-8 leading-relaxed">
                  Moc vám děkuji za podporu a těším se na každou zpětnou vazbu, která mi pomůže Věrnost dál zlepšovat. 🧡
                </p>
              </div>
            </div>

            {/* Right Column - Collage Grid */}
            <div className="lg:col-span-5 w-full flex justify-center mt-12 lg:mt-0">
              <div className="flex gap-4 w-full h-[400px] sm:h-[550px] relative z-10">
                
                {/* Left Large Photo (Photo 1) - Ivan */}
                <div className="w-[66%] h-full rounded-3xl overflow-hidden shadow-md border border-neutral-200/50">
                  <img 
                    src="/about_ivan.jpg" 
                    alt="Ivan v palačinkárně" 
                    className="w-full h-full object-cover object-center select-none pointer-events-none"
                    data-testid="about-img-ivan"
                  />
                </div>

                {/* Right Column (Photo 2 & Photo 3) */}
                <div className="w-[34%] flex flex-col gap-4 h-full">
                  {/* Top Right Photo (Photo 2) - Loyalty cards */}
                  <div className="h-[48%] rounded-2xl overflow-hidden shadow-md border border-neutral-200/50">
                    <img 
                      src="/about_cards.jpg" 
                      alt="Papírové věrnostní kartičky Don Pačínko" 
                      className="w-full h-full object-cover object-center select-none pointer-events-none"
                      data-testid="about-img-cards"
                    />
                  </div>

                  {/* Bottom Right Photo (Photo 3) - Customers draw */}
                  <div className="h-[48%] rounded-2xl overflow-hidden shadow-md border border-neutral-200/50">
                    <img 
                      src="/about_board.jpg" 
                      alt="Zprávy od zákazníků" 
                      className="w-full h-full object-cover object-center select-none pointer-events-none"
                      data-testid="about-img-board"
                    />
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* render footer */}
      <Footer handleScrollTo={handleScrollTo} pathname={pathname} />

      {/* Premium Activation Modal */}
      {isPremiumModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl relative border border-neutral-100 text-center animate-in zoom-in-95 duration-200">
            {/* Close button */}
            <button 
              onClick={() => setIsPremiumModalOpen(false)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-neutral-100 transition-colors text-neutral-400 hover:text-neutral-600"
              aria-label="Zavřít"
              data-testid="premium-modal-close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Sparkles icon */}
            <div className="w-12 h-12 rounded-full bg-[#FF5A00]/10 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-6 h-6 text-[#FF5A00]" />
            </div>

            {/* Modal Heading */}
            <h3 className="text-xl font-black text-neutral-900 mb-2">Aktivace Premium</h3>
            
            {/* Modal Description */}
            <p className="text-sm font-medium text-neutral-500 mb-6 leading-relaxed">
              Premium aktivujete přímo v aplikaci Věrnost.
            </p>

            {/* App Store / Google Play Buttons */}
            <div className="flex flex-col gap-3 w-full justify-center items-center">
              <a 
                href="https://app.vernostapp.cz"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsPremiumModalOpen(false)}
                className="w-full px-5 py-3 rounded-full bg-[#FF5A00] hover:bg-[#E04F00] text-white text-sm font-black flex items-center justify-center space-x-2.5 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-md"
                data-testid="premium-modal-open-web-app"
              >
                <span>Otevřít webovou aplikaci</span>
                <svg className="w-4 h-4 shrink-0 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                </svg>
              </a>
              <div className="flex flex-col sm:flex-row gap-3 w-full justify-center items-center">
                <a 
                  href="https://apps.apple.com/cz/app/v%C4%9Brnost/id6807436725?l=cs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full px-5 py-3 rounded-full bg-neutral-900 hover:bg-neutral-800 text-white text-sm font-bold flex items-center justify-center space-x-2.5 shadow-md border border-neutral-800 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                  data-testid="premium-modal-app-store"
                >
                  <svg className="w-4 h-4 fill-current shrink-0 text-white" viewBox="0 0 24 24">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.07 2.47.3 3.64 2.1-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42.14-1.38 1.1-1 .99-2 .98-2 .98-1.5 0-1.7-.79-3.29-.79-1.59 0-1.8.79-3.29.79-1.49 0-2.32-1.31-3.29-2.53M15.97 4.17c.66-.81 1.11-1.93.99-3.06-1 .04-2.11.67-2.83 1.49-.62.71-1.16 1.85-1.02 2.96 1.1.09 2.2-.58 2.86-1.39"/>
                  </svg>
                  <span className="text-white">App Store</span>
                </a>
                <div 
                  className="w-full px-5 py-3 rounded-full bg-neutral-900 text-white text-sm font-bold flex items-center justify-center space-x-2.5 shadow-md border border-neutral-800 opacity-75 select-none"
                  data-testid="premium-modal-google-play"
                >
                  <svg className="w-4 h-4 fill-current shrink-0 text-neutral-400" viewBox="0 0 24 24">
                    <path d="M3.25 2.5a1.25 1.25 0 0 0-.62 1.09v16.82a1.25 1.25 0 0 0 .62 1.09l9.38-9.5-9.38-9.5zm10.74 9.5l2.67-2.71-11.51-6.53 8.84 9.24zm3.43-3.48l2.96 1.68c.62.35.62.93 0 1.28l-2.96 1.68-2.67-2.7 2.67-2.94zm-3.43 5.48l-8.84 9.24 11.51-6.53-2.67-2.71z"/>
                  </svg>
                  <span className="text-neutral-300">Google Play</span>
                  <span className="text-[9px] uppercase bg-neutral-800 text-neutral-400 px-1.5 py-0.5 rounded font-extrabold tracking-wider">Již brzy</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Screenshot Lightbox Modal */}
      {activeScreenshot && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 md:p-8 animate-in fade-in duration-200"
          onClick={() => setActiveScreenshot(null)}
          data-testid="screenshot-lightbox"
        >
          {/* Close button X - fixed or absolute in top right */}
          <button 
            onClick={(e) => {
              e.stopPropagation();
              setActiveScreenshot(null);
            }}
            className="absolute top-4 right-4 md:top-6 md:right-6 p-2 rounded-full bg-neutral-900/60 hover:bg-neutral-800/80 transition-colors text-white"
            aria-label="Zavřít náhled"
            data-testid="lightbox-close"
          >
            <X className="w-6 h-6 md:w-8 md:h-8" />
          </button>

          {/* Screenshot Container to prevent clicking outside from closing when clicking the image */}
          <div 
            className="relative max-w-full max-h-full flex items-center justify-center animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={activeScreenshot.src} 
              alt={activeScreenshot.alt} 
              className="max-w-[95vw] md:max-w-[90vw] max-h-[85vh] md:max-h-[90vh] object-contain rounded-lg shadow-2xl border border-neutral-800/20"
              data-testid="lightbox-image"
            />
          </div>
        </div>
      )}

    </div>
  );
}

// ROUTER WRAPPER AND ROUTES CONFIG
export default function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

function AppRoutes() {
  const { handleScrollTo, pathname } = useAppNavigation();

  const renderHeader = () => (
    <Header handleScrollTo={handleScrollTo} pathname={pathname} />
  );

  const renderFooter = () => (
    <Footer handleScrollTo={handleScrollTo} pathname={pathname} />
  );

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route 
        path="/podminky-pouzivani" 
        element={<PodminkyPouzivani renderHeader={renderHeader} renderFooter={renderFooter} />} 
      />
      <Route 
        path="/ochrana-osobnich-udaju" 
        element={<OchranaOsobnichUdaju renderHeader={renderHeader} renderFooter={renderFooter} />} 
      />
      <Route 
        path="/podminky-pro-partnery" 
        element={<PodminkyProPartnery renderHeader={renderHeader} renderFooter={renderFooter} />} 
      />
      <Route 
        path="/podpora" 
        element={<Podpora renderHeader={renderHeader} renderFooter={renderFooter} />} 
      />
    </Routes>
  );
}
